import type { MenuItem } from '../types/workspace';
import type { PublicMenuSection } from './publicMenu';

export interface SearchSuggestion {
  id: string;
  type: 'item' | 'section' | 'time';
  title: string;
  subtitle: string;
  sectionId?: string;
  itemId?: string;
  query?: string;
  thumbnailSrc?: string;
  priceLabel?: string;
}

const normalize = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const tokenize = (value: string) => normalize(value).split(' ').filter(Boolean);

const levenshtein = (left: string, right: string) => {
  const rows = left.length + 1;
  const cols = right.length + 1;
  const matrix = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let row = 0; row < rows; row += 1) matrix[row][0] = row;
  for (let col = 0; col < cols; col += 1) matrix[0][col] = col;

  for (let row = 1; row < rows; row += 1) {
    for (let col = 1; col < cols; col += 1) {
      const cost = left[row - 1] === right[col - 1] ? 0 : 1;
      matrix[row][col] = Math.min(
        matrix[row - 1][col] + 1,
        matrix[row][col - 1] + 1,
        matrix[row - 1][col - 1] + cost,
      );
    }
  }

  return matrix[rows - 1][cols - 1];
};

const isSubsequence = (needle: string, haystack: string) => {
  let pointer = 0;
  for (const character of haystack) {
    if (character === needle[pointer]) {
      pointer += 1;
      if (pointer === needle.length) {
        return true;
      }
    }
  }
  return false;
};

const getNeedleScore = (needle: string, haystackParts: string[]) => {
  if (!needle) {
    return 0;
  }

  let bestScore = -1;

  haystackParts.forEach((part) => {
    if (!part) {
      return;
    }

    if (part === needle) {
      bestScore = Math.max(bestScore, 120);
      return;
    }

    if (part.startsWith(needle)) {
      bestScore = Math.max(bestScore, 100);
      return;
    }

    if (part.includes(needle)) {
      bestScore = Math.max(bestScore, 82);
      return;
    }

    const distance = levenshtein(needle, part);
    const allowedDistance = needle.length <= 4 ? 1 : 2;
    if (distance <= allowedDistance) {
      bestScore = Math.max(bestScore, 68 - distance * 8);
      return;
    }

    if (isSubsequence(needle, part)) {
      bestScore = Math.max(bestScore, 54);
    }
  });

  return bestScore;
};

export const getItemSearchScore = (item: MenuItem, section?: PublicMenuSection, rawQuery?: string) => {
  const query = normalize(rawQuery ?? '');
  if (!query) {
    return 1;
  }

  const haystackParts = [
    ...tokenize(item.name),
    ...tokenize(item.description),
    ...tokenize(section?.title ?? ''),
    ...tokenize(section?.shortDescription ?? ''),
  ];

  const needles = tokenize(query);
  if (needles.length === 0) {
    return 1;
  }

  const scores = needles.map((needle) => getNeedleScore(needle, haystackParts));
  const matchedScores = scores.filter((score) => score >= 0);

  if (matchedScores.length === 0) {
    return -1;
  }

  const minimumMatches = needles.length === 1 ? 1 : Math.max(1, Math.ceil(needles.length * 0.6));
  if (matchedScores.length < minimumMatches) {
    return -1;
  }

  return matchedScores.reduce((total, score) => total + score, 0) + matchedScores.length * 12;
};

const getDaypart = (hour: number) => {
  if (hour >= 5 && hour < 11) {
    return {
      title: 'Good morning',
      subtitle: 'Breakfast is the best place to start right now.',
      preferredSections: ['breakfast'],
    };
  }

  if (hour >= 11 && hour < 15) {
    return {
      title: 'Lunch picks',
      subtitle: 'Popular midday meals are ready to browse.',
      preferredSections: ['arabian-foods', 'beef', 'burger-sandwich'],
    };
  }

  if (hour >= 15 && hour < 18) {
    return {
      title: 'Afternoon break',
      subtitle: 'Juices, coffee, and lighter bites fit this time of day.',
      preferredSections: ['juice', 'hot-drinks', 'salad-vegetable'],
    };
  }

  return {
    title: 'Dinner ideas',
    subtitle: 'Heavier plates, pasta, pizza, and soup fit the evening.',
    preferredSections: ['pizza', 'spaghetti-rice-penne', 'soup', 'beef'],
  };
};

export const getSmartSuggestions = (
  sections: PublicMenuSection[],
  query: string,
  now = new Date(),
): SearchSuggestion[] => {
  const normalizedQuery = normalize(query);

  if (!normalizedQuery) {
    const daypart = getDaypart(now.getHours());
    const sectionMatches = sections
      .filter((section) => daypart.preferredSections.includes(section.id))
      .slice(0, 3);

    const timeSuggestion: SearchSuggestion = {
      id: `time-${daypart.title}`,
      type: 'time',
      title: daypart.title,
      subtitle: daypart.subtitle,
      sectionId: sectionMatches[0]?.id,
    };

    const sectionSuggestions = sectionMatches.map<SearchSuggestion>((section) => ({
      id: `section-${section.id}`,
      type: 'section',
      title: section.title,
      subtitle: `${section.items.length} items in this section`,
      sectionId: section.id,
      thumbnailSrc: section.coverImage,
    }));

    const itemSuggestions = sectionMatches
      .flatMap((section) => section.items.slice(0, 2).map((item) => ({ item, section })))
      .slice(0, 4)
      .map<SearchSuggestion>(({ item, section }) => ({
        id: `item-${item.id}`,
        type: 'item',
        title: item.name,
        subtitle: `${section.title} · ${item.price} ETB`,
        itemId: item.id,
        sectionId: section.id,
        thumbnailSrc: item.imageUrl || section.coverImage,
        priceLabel: `${item.price} ETB`,
      }));

    return [timeSuggestion, ...sectionSuggestions, ...itemSuggestions];
  }

  const itemResults = sections
    .flatMap((section) =>
      section.items.map((item) => ({
        item,
        section,
        score: getItemSearchScore(item, section, normalizedQuery),
      })),
    )
    .filter((entry) => entry.score >= 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, 6)
    .map<SearchSuggestion>(({ item, section }) => ({
      id: `item-${item.id}`,
      type: 'item',
      title: item.name,
      subtitle: `${section.title} · ${item.price} ETB`,
      itemId: item.id,
      sectionId: section.id,
      thumbnailSrc: item.imageUrl || section.coverImage,
      priceLabel: `${item.price} ETB`,
    }));

  const sectionResults = sections
    .map((section) => ({
      section,
      score: getNeedleScore(normalizedQuery, [
        ...tokenize(section.title),
        ...tokenize(section.shortDescription),
      ]),
    }))
    .filter((entry) => entry.score >= 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, 3)
    .map<SearchSuggestion>(({ section }) => ({
      id: `section-${section.id}`,
      type: 'section',
      title: section.title,
      subtitle: `${section.items.length} items in this section`,
      sectionId: section.id,
      thumbnailSrc: section.coverImage,
    }));

  return [...itemResults, ...sectionResults].slice(0, 7);
};
